/**
 * Роли и права административной панели. Проверяются на backend (guard PermissionsGuard);
 * фронтенд использует ту же карту только для скрытия недоступных элементов.
 */

export const ADMIN_ROLES = ['owner', 'admin', 'moderator', 'ads_manager'] as const;
export type AdminRole = (typeof ADMIN_ROLES)[number];

export const PERMISSIONS = [
  'stats.view',
  'listings.view',
  'listings.moderate',
  'reports.view',
  'reports.resolve',
  'users.view',
  'users.block',
  'categories.manage',
  'locations.manage',
  'translations.manage',
  'search.manage',
  'banners.manage',
  'promotions.manage',
  'business.manage',
  'settings.manage',
  'tariffs.manage',
  'payments.view',
  'payments.refund',
  'admins.manage',
  'audit.view',
  'deletion_requests.process',
  'system.view',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const MODERATOR: Permission[] = [
  'stats.view',
  'listings.view',
  'listings.moderate',
  'reports.view',
  'reports.resolve',
  'users.view',
  'users.block',
];

const ADS_MANAGER: Permission[] = ['stats.view', 'listings.view', 'banners.manage', 'promotions.manage', 'payments.view'];

const ADMIN: Permission[] = Array.from(
  new Set<Permission>([
    ...MODERATOR,
    ...ADS_MANAGER,
    'categories.manage',
    'locations.manage',
    'translations.manage',
    'search.manage',
    'settings.manage',
    'tariffs.manage',
    'audit.view',
    'deletion_requests.process',
    'business.manage',
    'payments.refund',
    'system.view',
  ]),
);

export const ROLE_PERMISSIONS: Record<AdminRole, readonly Permission[]> = {
  owner: PERMISSIONS,
  admin: ADMIN,
  moderator: MODERATOR,
  ads_manager: ADS_MANAGER,
};

export function hasPermission(role: AdminRole | null | undefined, permission: Permission): boolean {
  if (!role) return false;
  return ROLE_PERMISSIONS[role]?.includes(permission) ?? false;
}

/**
 * Кто кому может назначать роли: владелец — всем; администратор — только модераторам
 * и менеджерам рекламы; остальные — никому. Роль owner назначает только owner.
 */
export function canAssignRole(actor: AdminRole, target: AdminRole): boolean {
  if (actor === 'owner') return true;
  if (actor === 'admin') return target === 'moderator' || target === 'ads_manager';
  return false;
}

export interface PermissionOverride {
  role: AdminRole;
  permission: string;
  granted: boolean;
}

/**
 * Действующие права роли с учётом переопределений из админ-панели (таблица admin_role_permissions).
 * Владелец всегда имеет все права; право admins.manage выдаётся только владельцу — это защищает
 * от потери контроля над системой через изменение прав.
 */
export function effectivePermissions(role: AdminRole, overrides: readonly PermissionOverride[] = []): Permission[] {
  if (role === 'owner') return [...PERMISSIONS];
  const set = new Set<Permission>(ROLE_PERMISSIONS[role]);
  for (const o of overrides) {
    if (o.role !== role || !(PERMISSIONS as readonly string[]).includes(o.permission)) continue;
    const p = o.permission as Permission;
    if (p === 'admins.manage') continue;
    if (o.granted) set.add(p);
    else set.delete(p);
  }
  return PERMISSIONS.filter((p) => set.has(p));
}

/** Права, которые нельзя выдать другим ролям через админ-панель. */
export const OWNER_ONLY_PERMISSIONS: readonly Permission[] = ['admins.manage'];
